// src/utils/catalogSearch.js
//
// ── Design decisions ──────────────────────────────────────────────────────────
// Búsqueda de catálogo V2: por tokens, con vocabulario salvadoreño, y HONESTA.
//
// · Reemplaza la estrategia de consulta del typeahead (ILIKE de la frase
//   completa, que nunca acierta "brillos labiales" → LAPIZ LABIAL) por una
//   consulta .or() de un ILIKE por token y un ranking en cliente. No toca el
//   debounce ni la UI: AnexoItemRow sigue en su consulta hasta su propio delta.
// · Cada reinterpretación (jerga expandida, tier detectado) se devuelve en
//   `interpretaciones` para que la UI la muestre en un chip y el gestor la
//   confirme. Nada se corrige en silencio.
// · Tres funciones puras exportadas (normalizar, interpretarDescripcion,
//   rankear) sin supabase en su cadena: la única función que toca red es
//   buscarCatalogo, y supabase se importa solo para ella.
// · Nunca lanza hacia la UI: error de red ⇒ { resultados: [], error }.
// · Acentos: se quitan para comparar (á→a) pero la ñ se conserva en el token
//   de consulta porque el catálogo la escribe (NIÑO, PESTAÑOL). Postgres ILIKE
//   es sensible a tildes; el catálogo es mayormente sin tilde (PANTALON,
//   LAPIZ), así que el token sin tilde es la mejor apuesta. unaccent del lado
//   servidor queda fuera de alcance.
// · Singularización trivial: "-es" → "" (calcetines→calcetin, vanidades→
//   vanidad) y "-s" → "" (camisas→camisa). Como la consulta es por subcadena,
//   sobre-recortar es inofensivo; las excepciones conocidas (tenis) viven en
//   JERGA como identidad para que sigan siendo visibles y editables.
//
// Pesos del ranking (ver rankear):
//   +1    por cada token distinto que aparece en la descripción del catálogo
//   +0.5  si se detectó un tier y la fila trae la frase de ESE tier
//   −0.25 si la fila trae la frase de OTRO tier
//   Desempate: menos palabras en la fila (más específica al token), luego
//   alfabético — determinista para los tests y estable para el gestor.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from '../supabaseClient';
import { JERGA, MARCADORES_TIER, STOPWORDS } from './catalogSlang';

const LIMITE_FILAS_CONSULTA = 25;
const MIN_TOKEN = 2;

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

// Minúsculas, sin tildes (á é í ó ú ü → a e i o u u). Conserva la ñ.
// La ñ se aparta con una marca de uso privado (U+E000) antes de quitar los
// diacríticos, porque en NFD la ñ es n + U+0303 y se iría con el resto.
const MARCA_ENIE = '\uE000';
const ENIE = '\u00F1';
export const normalizar = (str) =>
  String(str || '')
    .toLowerCase()
    .split(ENIE).join(MARCA_ENIE)
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .split(MARCA_ENIE).join(ENIE)
    .trim();

// Versión sin ñ para comparar contra filas del catálogo ya normalizadas.
const sinEnie = (str) => str.replace(/ñ/g, 'n');

export const singularizar = (token) => {
  if (token.length > 4 && token.endsWith('es')) return token.slice(0, -2);
  if (token.length > 3 && token.endsWith('s')) return token.slice(0, -1);
  return token;
};

const esStopword = (tok) => STOPWORDS.includes(tok);

// ── Pasos 1–5: puro, sin red ─────────────────────────────────────────────────
// Devuelve { tokens, tier, interpretaciones, original }.
//   tokens: lista de tokens normalizados, singularizados, sin stopwords ni tier.
//   tier:   'otras' | 'prestigio' | 'lujo' | null
export function interpretarDescripcion(descripcion) {
  const original = String(descripcion || '');
  let texto = normalizar(original);
  const interpretaciones = [];
  let tier = null;

  // (2) Marcadores de tier: se buscan sobre la frase completa (pueden tener
  // varias palabras o barras), se registran y se quitan del flujo.
  const tiers = Object.entries(MARCADORES_TIER);
  const patrones = tiers
    .flatMap(([clave, def]) => def.patrones.map((p) => ({ clave, patron: normalizar(p), etiqueta: def.etiqueta })))
    .sort((a, b) => b.patron.length - a.patron.length);
  for (const { clave, patron, etiqueta } of patrones) {
    // (?<![a-z0-9ñ]) en vez de \b: \b no funciona al borde de "/" ni ".".
    const re = new RegExp(`(?<![a-z0-9ñ])${escapeRegex(patron)}(?![a-z0-9ñ])`, 'g');
    if (re.test(texto)) {
      if (!tier) {
        tier = clave;
        interpretaciones.push({ original: patron, interpretado: etiqueta, tipo: 'marca' });
      }
      texto = texto.replace(re, ' ');
    }
  }

  // Tokenizar: espacios, barras, paréntesis y puntuación separan.
  const crudos = texto.split(/[\s/(),;:+"'«»¿?¡!]+/).filter(Boolean);

  const tokens = [];
  for (const crudo of crudos) {
    const tok = crudo.replace(/^[.\-_]+|[.\-_]+$/g, '');
    if (!tok) continue;

    // (3) Jerga: se expande y se registra. La clave puede estar en plural.
    const claveJerga = [tok, singularizar(tok)].find((k) => Object.prototype.hasOwnProperty.call(JERGA, k));
    if (claveJerga) {
      const formal = JERGA[claveJerga];
      if (normalizar(formal) !== tok) {
        interpretaciones.push({ original: crudo, interpretado: formal, tipo: 'jerga' });
      }
      // El término formal ya es vocabulario de catálogo: no se singulariza
      // (si no, "tenis" → "teni" y deja de coincidir).
      normalizar(formal)
        .split(/\s+/)
        .filter((t) => t && !esStopword(t))
        .forEach((t) => tokens.push(t));
      continue;
    }

    // (4) Stopwords y tokens demasiado cortos.
    if (esStopword(tok) || tok.length < MIN_TOKEN) continue;

    // (5) Singular trivial.
    tokens.push(singularizar(tok));
  }

  // Sin duplicados, orden de aparición.
  const unicos = [...new Set(tokens)];
  return { original, tokens: unicos, tier, interpretaciones };
}

// ── Paso 7: ranking en cliente, puro ─────────────────────────────────────────
export function rankear(filas, tokens, tier, { limit = 3 } = {}) {
  const toks = (tokens || []).map((t) => sinEnie(normalizar(t)));
  const puntuadas = (filas || []).map((fila) => {
    const desc = sinEnie(normalizar(fila.descripcion));
    const tokensCoincididos = toks.filter((t) => desc.includes(t));
    let score = tokensCoincididos.length;
    if (tier) {
      for (const [clave, def] of Object.entries(MARCADORES_TIER)) {
        const trae = def.frases.some((f) => desc.includes(sinEnie(normalizar(f))));
        if (!trae) continue;
        score += clave === tier ? 0.5 : -0.25;
      }
    }
    return {
      id: fila.id,
      descripcion: fila.descripcion,
      cip_usd: fila.cip_usd ?? null,
      unidad_medida: fila.unidad_medida ?? null,
      fob_usd: fila.fob_usd ?? null,
      score: Math.round(score * 100) / 100,
      tokensCoincididos,
    };
  });

  return puntuadas
    .filter((f) => f.tokensCoincididos.length > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const la = String(a.descripcion).split(/\s+/).length;
      const lb = String(b.descripcion).split(/\s+/).length;
      if (la !== lb) return la - lb;
      return String(a.descripcion).localeCompare(String(b.descripcion));
    })
    .slice(0, limit);
}

// Token de consulta: sin tildes, CON ñ (el catálogo la escribe). Se escapan
// los comodines de ILIKE por si el gestor tecleó % o _.
const tokenConsulta = (t) => t.replace(/[%_]/g, '');

// ── Paso 6 + 7 + 8: la búsqueda ──────────────────────────────────────────────
export async function buscarCatalogo(descripcion, { limit = 3 } = {}) {
  const { tokens, tier, interpretaciones } = interpretarDescripcion(descripcion);

  if (tokens.length === 0) {
    return { resultados: [], interpretaciones: [] };
  }

  const or = tokens
    .map(tokenConsulta)
    .filter(Boolean)
    .map((t) => `descripcion.ilike.%${t}%`)
    .join(',');

  try {
    const { data, error } = await supabase
      .from('customs_catalog')
      .select('id, descripcion, unidad_medida, fob_usd, cip_usd')
      .or(or)
      .limit(LIMITE_FILAS_CONSULTA);
    if (error) throw error;
    return {
      resultados: rankear(data || [], tokens, tier, { limit }),
      interpretaciones,
    };
  } catch (err) {
    console.error('[catalogSearch] error consultando customs_catalog', err);
    return {
      resultados: [],
      interpretaciones,
      error: err?.message || 'No se pudo consultar el catálogo.',
    };
  }
}
